import { AspectRatioValues, type CropArea, type ImageTransform } from '../types'

export const DEFAULT_ASPECT_RATIOS = [
  { label: '1:1', value: AspectRatioValues.ONE_TO_ONE, ratio: 1 },
  { label: '4:3', value: AspectRatioValues.FOUR_TO_THREE, ratio: 4 / 3 },
  { label: '16:9', value: AspectRatioValues.SIXTEEN_TO_NINE, ratio: 16 / 9 },
  { label: '5:2 Cover (Recommended)', value: AspectRatioValues.COVER, ratio: 5 / 2 },
  { label: 'Freeform', value: AspectRatioValues.FREE, ratio: undefined },
]

export function getAspectRatioValue(aspectRatio: AspectRatioValues): number | undefined {
  const ratio = DEFAULT_ASPECT_RATIOS.find((r) => r.value === aspectRatio)
  return ratio?.ratio
}

function createImage(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new Image()
    image.addEventListener('load', () => resolve(image))
    image.addEventListener('error', (error) => reject(error))
    image.src = url
  })
}

function createBlobFromCanvas(canvas: HTMLCanvasElement): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) {
          resolve(blob)
        } else {
          reject(new Error('Canvas to blob conversion failed'))
        }
      },
      'image/webp',
      0.95,
    )
  })
}

export async function getCroppedImg(imageSrc: string, crop: CropArea): Promise<Blob> {
  const image = await createImage(imageSrc)
  const { naturalWidth: imgWidth, naturalHeight: imgHeight } = image

  const pixelCrop = {
    x: Math.round((crop.x / 100) * imgWidth),
    y: Math.round((crop.y / 100) * imgHeight),
    width: Math.round((crop.width / 100) * imgWidth),
    height: Math.round((crop.height / 100) * imgHeight),
  }

  const boundedCrop = {
    x: Math.max(0, Math.min(pixelCrop.x, imgWidth - 1)),
    y: Math.max(0, Math.min(pixelCrop.y, imgHeight - 1)),
    width: Math.max(1, Math.min(pixelCrop.width, imgWidth - pixelCrop.x)),
    height: Math.max(1, Math.min(pixelCrop.height, imgHeight - pixelCrop.y)),
  }

  const cropCanvas = document.createElement('canvas')
  const cropCtx = cropCanvas.getContext('2d')

  if (!cropCtx) {
    throw new Error('No 2d context')
  }

  cropCanvas.width = boundedCrop.width
  cropCanvas.height = boundedCrop.height

  cropCtx.drawImage(
    image,
    boundedCrop.x,
    boundedCrop.y,
    boundedCrop.width,
    boundedCrop.height,
    0,
    0,
    boundedCrop.width,
    boundedCrop.height,
  )

  return createBlobFromCanvas(cropCanvas)
}

export const DEFAULT_TRANSFORM: ImageTransform = {
  crop: { x: 25, y: 25, width: 50, height: 50 },
  rotation: 0,
  flipHorizontal: false,
  flipVertical: false,
  aspectRatio: AspectRatioValues.FREE,
  zoom: 1,
}
